import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikProps } from 'formik';
import { LoadingOutlined } from '@ant-design/icons';

import { I18nNameSpaces, Languages } from 'common/services/i18n';
import { ClientVerificationFormPhoneValues } from 'common/models/Forms';
import { InputSearchField, PhoneInput } from 'common/fields';
import { ResultMessage } from 'common/components';
import { makePhoneSchema, makeCodeSchema } from './schema';

interface Props {
  success?: string | null;
  error?: string | null;
  loadingRequest?: boolean;
  loadingVerify?: boolean;
  initialValues?: Partial<ClientVerificationFormPhoneValues>;
  disableVerify?: boolean;

  onRequestCode: (phone: ClientVerificationFormPhoneValues['phone']) => void;
  onVerifyCode: (code: string) => void;
  onSubmit?: (values: ClientVerificationFormPhoneValues) => void;
}

export default function ClientPhoneVerificationForm({
  success,
  error,
  loadingRequest,
  loadingVerify,
  initialValues,
  disableVerify,
  onRequestCode,
  onVerifyCode,
}: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation(I18nNameSpaces.Forms);

  const phoneSchema = makePhoneSchema(t);
  const codeSchema = makeCodeSchema(t);

  function renderCodeRequestForm({ submitForm }: FormikProps<Pick<ClientVerificationFormPhoneValues, 'phone'>>) {
    return (
      <Form>
        <PhoneInput
          name="phone"
          label={t('clientForm.phone.label')}
          placeholder={t('clientForm.phone.placeholder')}
          enterButton={loadingRequest ? <LoadingOutlined /> : t('verificationForm.sms.request.submit')}
          onSearch={submitForm}
        />
      </Form>
    );
  }

  function renderCodeVerifyForm({ submitForm }: FormikProps<Pick<ClientVerificationFormPhoneValues, 'code'>>) {
    return (
      <Form>
        <InputSearchField
          name="code"
          label={t('verificationForm.sms.code.label')}
          placeholder={t('verificationForm.sms.code.placeholder')}
          autoComplete="off"
          enterButton={loadingVerify ? <LoadingOutlined /> : t('verificationForm.sms.verify.submit')}
          onSearch={submitForm}
          disabled={loadingVerify || disableVerify}
        />
      </Form>
    );
  }

  return (
    <div className="client-phone-verification-form">
      <Formik
        initialValues={{
          phone: initialValues?.phone ?? {
            number: '',
            prefix: language === Languages.Czech ? '420' : '421',
          },
        }}
        onSubmit={({ phone }) => {
          onRequestCode(phone);
        }}
        validationSchema={phoneSchema}
        validateOnBlur={false}
        validateOnChange={false}
        enableReinitialize
      >
        {renderCodeRequestForm}
      </Formik>
      <Formik
        initialValues={{
          code: '',
        }}
        onSubmit={({ code }) => {
          onVerifyCode(code);
        }}
        validationSchema={codeSchema}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {renderCodeVerifyForm}
      </Formik>
      {Boolean(error) && <ResultMessage type="error">{error}</ResultMessage>}
      {Boolean(success) && !error && <ResultMessage type="success">{success}</ResultMessage>}
    </div>
  );
}
